import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ClrCommonFormsModule, ClrIconModule } from "@clr/angular";
import { NgIf } from "@angular/common";
import { ClarityIcons, floppyIcon } from "@cds/core/icon";
import { AlertService, IAlertType, StaffStoreService } from "../../../../_shared/services";
import { AuthUser, Staff, WebserviceResponse } from "../../../../_shared/interfaces";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

@Component({
  selector: 'app-edit-staff',
  standalone: true,
  imports: [
    ClrCommonFormsModule,
    ReactiveFormsModule,
    NgIf,
    ClrIconModule
  ],
  templateUrl: './edit-staff.component.html',
  styleUrl: './edit-staff.component.sass'
})
export class EditStaffComponent implements OnInit {
  @Input() editStaff = 0;
  @Output() updatedStaff = new EventEmitter<boolean>();

  editStaffForm!: FormGroup;
  staffItem: Staff | null = null;
  authUser: AuthUser | null = null;

  constructor(
    private staffService: StaffStoreService,
    private fb: FormBuilder,
    private alertDispatcher: AlertService
  ) {
    ClarityIcons.addIcons(floppyIcon);
    this.editStaffForm = this.fb.group({
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      pps_id: [''],
      crm_id: [''],
      active: [true],
      is_admin: [false],
      crm_username: [''],
      auth_password: ['']
    });
  }

  ngOnInit(): void {
    this.loadStaff(this.editStaff);
  }

  loadStaff(staffId: number) {
    this.staffService.getStaffById(staffId).subscribe({
      next: (res: WebserviceResponse<Staff[]>) => {
        const item = res?.object?.[0];
        if (!item) {
          this.alertDispatcher.show({
            message: 'Mitarbeiter konnte nicht geladen werden.',
            clrAlertType: IAlertType.WARNING
          }, 4000);
          return;
        }
        this.staffItem = item;
        this.editStaffForm.patchValue({
          firstname: item.firstname,
          lastname: item.lastname,
          email: item.email,
          phone: item.phone,
          pps_id: item.pps_id ?? '',
          crm_id: item.crm_id ?? '',
          active: item.active ?? true,
          is_admin: false
        });
        this.staffService.getAuthUsers().subscribe({
          next: (users: AuthUser[]) => {
            this.authUser = users.find((u) => u.staff_id === staffId) ?? null;
            this.editStaffForm.patchValue({
              is_admin: this.authUser?.is_admin ?? false,
              crm_username: this.authUser?.crm_username ?? ''
            });
          }
        });
      }
    });
  }

  updateStaff() {
    if (this.editStaffForm.invalid) return;
    const authPassword = (this.editStaffForm.value.auth_password ?? '').toString().trim();
    const rmsPayload = {
      firstname: this.editStaffForm.value.firstname,
      lastname: this.editStaffForm.value.lastname,
      email: this.editStaffForm.value.email,
      phone: this.editStaffForm.value.phone,
      pps_id: this.editStaffForm.value.pps_id,
      crm_id: this.editStaffForm.value.crm_id,
      active: this.editStaffForm.value.active
    };

    this.staffService.updateStaff(this.editStaff, rmsPayload).pipe(
      switchMap((res) => {
        if (res?.hasErrors) {
          this.alertDispatcher.show({
            message: 'Mitarbeiter konnte nicht aktualisiert werden.',
            clrAlertType: IAlertType.WARNING
          }, 4000);
          return of(null);
        }

        const displayName = `${this.editStaffForm.value.firstname} ${this.editStaffForm.value.lastname}`.trim();
        const crmUsername = (this.editStaffForm.value.crm_username ?? '').trim() || null;
        if (this.authUser?.id) {
          // Ein gefuelltes Passwortfeld setzt hier ein neues Passwort. Vorher
          // wurde es in diesem Zweig stillschweigend verworfen, sodass sich das
          // Passwort eines bestehenden Benutzers gar nicht aendern liess.
          return this.staffService.updateAuthUser(this.authUser.id, {
            email: this.editStaffForm.value.email,
            display_name: displayName,
            crm_username: crmUsername,
            is_active: this.editStaffForm.value.active,
            is_admin: this.editStaffForm.value.is_admin,
            staff_id: this.editStaff,
            ...(authPassword ? { password: authPassword } : {})
          }).pipe(
            map(() => true),
            catchError(() => {
              this.alertDispatcher.show({
                message: 'Mitarbeiter aktualisiert, aber Auth-User konnte nicht aktualisiert werden.',
                clrAlertType: IAlertType.WARNING
              }, 5000);
              return of(true);
            })
          );
        }

        if (authPassword) {
          return this.staffService.createAuthUser({
            email: this.editStaffForm.value.email,
            password: authPassword,
            display_name: displayName,
            crm_username: crmUsername,
            is_active: this.editStaffForm.value.active,
            is_admin: this.editStaffForm.value.is_admin,
            staff_id: this.editStaff
          }).pipe(
            map(() => true),
            catchError(() => {
              this.alertDispatcher.show({
                message: 'Mitarbeiter aktualisiert, aber Auth-User konnte nicht angelegt werden.',
                clrAlertType: IAlertType.WARNING
              }, 5000);
              return of(true);
            })
          );
        }

        return of(true);
      })
    ).subscribe({
      next: (ok) => {
        if (!ok) return;
        this.editStaffForm.patchValue({ auth_password: '' });
        this.alertDispatcher.show({
          message: authPassword
            ? 'Mitarbeiter aktualisiert, neues Passwort gesetzt. Bestehende Sitzungen wurden beendet.'
            : 'Mitarbeiter wurde aktualisiert.',
          clrAlertType: IAlertType.SUCCESS
        }, authPassword ? 5000 : 3000);
        this.updatedStaff.emit(true);
      }
    });
  }

  cancelEdit() {
    this.updatedStaff.emit(true);
  }
}
